import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Icon } from "@iconify/react";
import { cn } from "lib/utils";
import { Loader2, MoreVertical } from "lucide-react";

import {
  useDownloadCreditReportMetadata,
  useDownloadInvestigationCreditReport,
  useDownloadInvestigationMultireport,
  useInvestigations,
  useRetryLongReport,
  useSendCreditReport,
  useSendInvestigationMultireport,
  useSendMetadata,
  useToggleInvestigationStatus,
} from "hooks/useInvestigations";

import ShowInfoModal from "js/components/Dashboard/ShowInfoModal";
import { Investigation } from "js/components/ExpressReport/types";
import { Advisor, PaginationTable, Person } from "js/components/types";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "js/components/ui/alert-dialog";
import { Badge } from "js/components/ui/badge";
import { Button } from "js/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "js/components/ui/command";
import { TablePagination } from "js/components/ui/pagination";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "js/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "js/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "js/components/ui/tooltip";

interface InvestigationTableProps {
  showAdvisorColumn: boolean;
  searchFilter?: string;
  isOrganization?: boolean;
  additionalFilters?: {
    is_active?: string;
    status?: string;
    legal_type?: string;
  };
}

type Column = {
  key: string;
  header: string;
  width?: string;
  align?: "left" | "center" | "right";
  render: (investigation: Investigation) => React.ReactNode;
};

const InvestigationTable: React.FC<InvestigationTableProps> = ({
  showAdvisorColumn,
  searchFilter = "",
  isOrganization = false,
  additionalFilters = {},
}) => {
  const [pagination, setPagination] = useState<PaginationTable>({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const { data: investigationsData, isLoading } = useInvestigations({
    pagination,
    filter: searchFilter,
    additionalFilters,
    isOrganization,
  });

  const toggleStatusMutation = useToggleInvestigationStatus();
  const retryLongReportMutation = useRetryLongReport();

  // Download mutations
  const downloadMultireportMutation = useDownloadInvestigationMultireport();
  const downloadCreditReportMutation = useDownloadInvestigationCreditReport();
  const downloadMetadataMutation = useDownloadCreditReportMetadata();

  // Send mutations
  const sendMultireportMutation = useSendInvestigationMultireport();
  const sendCreditReportMutation = useSendCreditReport();
  const sendMetadataMutation = useSendMetadata();

  const [infoModalInvestigation, setInfoModalInvestigation] =
    useState<Investigation | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [investigationToToggle, setInvestigationToToggle] =
    useState<Investigation | null>(null);
  const [openPopovers, setOpenPopovers] = useState<Record<string, boolean>>({});

  // Derive loading states from mutations
  const isDownloading =
    downloadMultireportMutation.isPending ||
    downloadCreditReportMutation.isPending ||
    downloadMetadataMutation.isPending;

  const isSending =
    sendMultireportMutation.isPending ||
    sendCreditReportMutation.isPending ||
    sendMetadataMutation.isPending;

  useEffect(() => {
    if (!investigationsData?.count && investigationsData?.count !== 0) return;

    setPagination((prev) => {
      const total = investigationsData.count;
      const totalPages = Math.max(1, Math.ceil(total / prev.pageSize));
      const nextCurrent = Math.min(prev.current, totalPages);
      if (prev.total === total && prev.current === nextCurrent) return prev;
      return {
        ...prev,
        total,
        current: nextCurrent,
      };
    });
  }, [investigationsData?.count]);

  const data = investigationsData?.results ?? [];

  const handleDeactivate = async () => {
    if (!investigationToToggle) return;
    await toggleStatusMutation.mutateAsync({
      investigationId: investigationToToggle.id,
      action: "deactivate",
    });
    setConfirmOpen(false);
    setInvestigationToToggle(null);
  };

  const closePopover = (investigationId: string) => {
    setOpenPopovers((prev) => ({ ...prev, [investigationId]: false }));
  };

  const columns: Column[] = useMemo(() => {
    const base: Column[] = [
      {
        key: "status",
        header: "Estatus",
        width: "100px",
        align: "center",
        render: (investigation) => (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex items-center justify-center">
                <Icon
                  icon={
                    investigation.is_active
                      ? "ion:checkmark-circle"
                      : "ion:close-circle"
                  }
                  fontSize={20}
                  color={investigation.is_active ? "#10b981" : "#d4d4d8"}
                />
              </span>
            </TooltipTrigger>
            <TooltipContent side="top">
              {investigation.is_active ? "Activo" : "Inactivo"}
            </TooltipContent>
          </Tooltip>
        ),
      },
      {
        key: "email",
        header: "Correo",
        width: "220px",
        render: (investigation) => (
          <button
            type="button"
            onClick={() => setInfoModalInvestigation(investigation)}
            className="sentry-mask line-clamp-1 text-left font-semibold text-slate-700 transition-colors hover:text-[#0060ff]"
          >
            {investigation.prospectus_email || ""}
          </button>
        ),
      },
      {
        key: "person",
        header: "Solicitante",
        width: "240px",
        render: (investigation) => {
          const person: Person | undefined = investigation.person;
          if (person?.type === "Legal") {
            return <span className="sentry-mask">{person.legal_entity_name}</span>;
          }
          const name = [
            person?.name,
            person?.first_surname,
            person?.second_surname,
          ]
            .filter(Boolean)
            .join(" ");
          return <span className="sentry-mask">{name || ""}</span>;
        },
      },
      {
        key: "legal_type",
        header: "Tipo Persona",
        width: "140px",
        align: "center",
        render: (investigation) => {
          const normalized = (investigation.legal_type || "").toLowerCase();
          const isMoral = normalized === "legal" || normalized === "moral";
          return (
            <Badge
              variant="outline"
              className={cn(
                "border-emerald-200 text-emerald-600",
                isMoral && "border-sky-200 text-sky-600",
              )}
            >
              {isMoral ? "Moral" : "Física"}
            </Badge>
          );
        },
      },
      {
        key: "status_detail",
        header: "Fase",
        width: "160px",
        align: "center",
        render: (investigation) => {
          if (
            investigation.status === "in_progress" &&
            investigation.credit_report.status === "capturing_inputs"
          ) {
            return "Capturando Información";
          }

          switch (investigation.credit_report?.status) {
            case "capturing_inputs":
              return "Iniciado";
            case "pin_validation":
              return "Confirmando NIP";
            case "completed":
              return "Completado";
            case "long_report":
              return "Esperando Reporte Extenso";
            case "invalid":
              return "Invalidado";
            default:
              return "Estado Desconocido";
          }
        },
      },
      {
        key: "created_at",
        header: "Fecha de Creación",
        width: "180px",
        align: "center",
        render: (investigation) =>
          new Date(investigation.created_at).toLocaleDateString("es-ES", {
            year: "numeric",
            month: "long",
            day: "numeric",
          }),
      },
      {
        key: "actions",
        header: "Acciones",
        width: "120px",
        align: "center",
        render: (investigation) => {
          const completed = investigation.status === "completed";
          const waitingLong = investigation.status === "Waiting long Report";

          const sections = [
            {
              heading: "Acceso",
              items: [
                {
                  key: "link-report",
                  label: "Abrir link de reporte",
                  icon: "solar:link-circle-linear",
                },
                {
                  key: "view-info",
                  label: "Ver información",
                  icon: "ion:information-circle-outline",
                },
                {
                  key: "retry",
                  label: "Actualizar Reporte Largo",
                  icon: "tabler:refresh",
                  disabled: !waitingLong,
                },
              ],
            },
            {
              heading: "Estado",
              items: [
                {
                  key: investigation.is_active ? "deactivate" : "activate",
                  label: investigation.is_active ? "Desactivar" : "Activar",
                  icon: investigation.is_active
                    ? "solar:power-bold"
                    : "mdi:power-cycle",
                  disabled: completed,
                },
              ],
            },
            {
              heading: "Descargas",
              items: [
                {
                  key: "download-multi",
                  label: "Descargar Multireporte",
                  icon: "ion:cloud-download-outline",
                  disabled: !completed || isDownloading,
                },
                {
                  key: "download-hc",
                  label: "Descargar Historial Crediticio",
                  icon: "ion:document-text-outline",
                  disabled: !completed || isDownloading,
                },
                {
                  key: "download-metadata",
                  label: "Descargar Metadata",
                  icon: "ion:server-outline",
                  disabled: !completed || isDownloading,
                },
              ],
            },
            {
              heading: "Enviar",
              items: [
                {
                  key: "send-multi",
                  label: "Enviar Multireporte",
                  icon: "ion:send-outline",
                  disabled: !completed || isSending,
                },
                {
                  key: "send-hc",
                  label: "Enviar Historial Crediticio",
                  icon: "ion:send-outline",
                  disabled: !completed || isSending,
                },
                {
                  key: "send-metadata",
                  label: "Enviar Metadata",
                  icon: "ion:send-outline",
                  disabled: !completed || isSending,
                },
              ],
            },
          ];

          const handleSelect = async (key: string) => {
            if (key === "activate") {
              await toggleStatusMutation.mutateAsync({
                investigationId: investigation.id,
                action: "activate",
              });
              closePopover(investigation.id);
            } else if (key === "deactivate") {
              setInvestigationToToggle(investigation);
              setConfirmOpen(true);
              closePopover(investigation.id);
            } else if (key === "link-report") {
              const w = window.open(
                `/reports/${investigation.id}`,
                "_blank",
                "noopener,noreferrer",
              );
              if (w) w.opener = null;
              closePopover(investigation.id);
            } else if (key === "view-info") {
              setInfoModalInvestigation(investigation);
              closePopover(investigation.id);
            } else if (key === "retry") {
              await retryLongReportMutation.mutateAsync(investigation.id);
              closePopover(investigation.id);
            } else if (key === "download-multi") {
              downloadMultireportMutation.mutate(investigation.id);
              closePopover(investigation.id);
            } else if (key === "download-hc") {
              downloadCreditReportMutation.mutate(investigation.id);
              closePopover(investigation.id);
            } else if (key === "download-metadata") {
              downloadMetadataMutation.mutate(investigation.id);
              closePopover(investigation.id);
            } else if (key === "send-multi") {
              sendMultireportMutation.mutate(investigation.id);
              closePopover(investigation.id);
            } else if (key === "send-hc") {
              sendCreditReportMutation.mutate(investigation.id);
              closePopover(investigation.id);
            } else if (key === "send-metadata") {
              sendMetadataMutation.mutate(investigation.id);
              closePopover(investigation.id);
            }
          };

          return (
            <Popover
              open={openPopovers[investigation.id] ?? false}
              onOpenChange={(open) =>
                setOpenPopovers((prev) => ({
                  ...prev,
                  [investigation.id]: open,
                }))
              }
            >
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-full border border-transparent text-slate-500 hover:border-slate-200 hover:bg-slate-100"
                >
                  <MoreVertical className="size-4" />
                  <span className="sr-only">Abrir menú de acciones</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-64 p-0">
                <Command>
                  <CommandList className="max-h-none">
                    <CommandEmpty>Sin acciones disponibles</CommandEmpty>
                    {sections.map((section, index) => (
                      <React.Fragment key={section.heading}>
                        {index > 0 && <CommandSeparator />}
                        <CommandGroup heading={section.heading}>
                          {section.items.map((item) => (
                            <CommandItem
                              key={item.key}
                              value={item.key}
                              disabled={item.disabled}
                              onSelect={async (current) => {
                                await handleSelect(current);
                              }}
                              className="cursor-pointer"
                            >
                              <Icon
                                icon={item.icon}
                                fontSize={16}
                                className="text-slate-400"
                              />
                              {item.label}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </React.Fragment>
                    ))}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          );
        },
      },
    ];

    if (showAdvisorColumn) {
      base.unshift({
        key: "advisor",
        header: "Asesor",
        width: "160px",
        render: (investigation) => (
          <span className="sentry-mask">
            {(investigation.advisor as Advisor)?.full_name ?? "Huérfano"}
          </span>
        ),
      });
    }

    return base;
  }, [
    showAdvisorColumn,
    isDownloading,
    isSending,
    toggleStatusMutation,
    retryLongReportMutation,
    downloadMultireportMutation,
    downloadCreditReportMutation,
    downloadMetadataMutation,
    sendMultireportMutation,
    sendCreditReportMutation,
    sendMetadataMutation,
  ]);

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      total: investigationsData?.count ?? prev.total,
    }));
  };

  const columnCount = columns.length;

  return (
    <>
      <div className="space-y-4">
        <div className="overflow-hidden rounded-xl border border-slate-200/70 bg-white">
          <Table className="min-w-[960px]">
            <TableHeader>
              <TableRow className="bg-slate-50/80">
                {columns.map((column) => (
                  <TableHead
                    key={column.key}
                    className={cn(
                      column.align === "center" && "text-center",
                      column.align === "right" && "text-right",
                    )}
                    style={{ width: column.width }}
                  >
                    {column.header}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={columnCount} className="text-center">
                    <div className="flex items-center justify-center gap-2 py-10 text-slate-500">
                      <Loader2 className="size-5 animate-spin" />
                      Cargando investigaciones...
                    </div>
                  </TableCell>
                </TableRow>
              ) : data.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columnCount}
                    className="py-10 text-center text-slate-500"
                  >
                    No se encontraron investigaciones.
                  </TableCell>
                </TableRow>
              ) : (
                data.map((investigation) => (
                  <TableRow key={investigation.id} className="bg-white/60">
                    {columns.map((column) => (
                      <TableCell
                        key={column.key}
                        className={cn(
                          "align-middle",
                          column.align === "center" && "text-center",
                          column.align === "right" && "text-right",
                        )}
                      >
                        {column.render(investigation)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        <TablePagination
          pagination={pagination}
          onChange={handlePageChange}
          isLoading={isLoading}
        />
      </div>

      {infoModalInvestigation && (
        <ShowInfoModal
          report={infoModalInvestigation}
          isModalVisible={true}
          setIsModalVisible={(visible: boolean) => {
            if (!visible) setInfoModalInvestigation(null);
          }}
        />
      )}

      <AlertDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmOpen(false);
            setInvestigationToToggle(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desactivar investigación</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro que deseas desactivar esta investigación? Los cobros
              hechos no son reembolsables.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              onClick={handleDeactivate}
            >
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default InvestigationTable;
